import React, { useState, useEffect, useRef } from 'react';
import { Check, Building2, Users, Heart, TrendingUp, Shield, Clock, Phone, Mail, ArrowRight, ChevronDown, ChevronUp, Star, Zap, Target, Award, Plus, Minus, Calculator, DollarSign, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Animated Hero Illustration Component
const AnimatedHeroIllustration = () => {
  const [time, setTime] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(t => t + 0.05);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Calculate positions for animated elements
  const pulse1 = Math.sin(time) * 5;
  const pulse2 = Math.sin(time + 1) * 5;
  const pulse3 = Math.sin(time + 2) * 5;

  const float1Y = Math.sin(time * 0.8) * 10;
  const float2Y = Math.sin(time * 0.6 + 1) * 12;
  const float3Y = Math.sin(time * 0.7 + 2) * 8;

  const rotate = time * 20;

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '450px',
      overflow: 'visible'
    }}>
      {/* Animated background circles */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) scale(${1 + Math.sin(time * 0.5) * 0.1})`,
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(244, 206, 123, 0.15) 0%, transparent 70%)',
        transition: 'transform 0.5s ease'
      }}></div>

      <svg viewBox="0 0 600 450" style={{ width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id="blueGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#286CFF" />
            <stop offset="100%" stopColor="#133E97" />
          </linearGradient>
          <linearGradient id="greenGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#28C08A" />
            <stop offset="100%" stopColor="#20D9A0" />
          </linearGradient>
          <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFD98E" />
            <stop offset="100%" stopColor="#F4CE7B" />
          </linearGradient>
        </defs>

        {/* Background glow circles */}
        <circle cx="300" cy="225" r="200" fill="#E3EDFF" opacity="0.2" />

        {/* Animated network connections */}
        <g opacity={0.3 + Math.sin(time) * 0.2}>
          <line x1="150" y1="150" x2="450" y2="150" stroke="#286CFF" strokeWidth="2" strokeDasharray="5,5" />
          <line x1="150" y1="300" x2="450" y2="300" stroke="#286CFF" strokeWidth="2" strokeDasharray="5,5" />
          <line x1="300" y1="100" x2="300" y2="350" stroke="#286CFF" strokeWidth="2" strokeDasharray="5,5" />
        </g>

        {/* Central Healthcare Hub */}
        <g transform={`translate(300, 225) scale(${1 + pulse1 * 0.01})`}>
          <circle cx="0" cy="0" r="70" fill="url(#blueGrad)" opacity="0.9" />
          <circle cx="0" cy="0" r="55" fill="white" opacity="0.95" />

          {/* Animated Plus Sign (Healthcare Symbol) */}
          <rect x="-25" y="-8" width="50" height="16" rx="4" fill="url(#blueGrad)" />
          <rect x="-8" y="-25" width="16" height="50" rx="4" fill="url(#blueGrad)" />

          {/* Pulsing rings */}
          <circle cx="0" cy="0" r={80 + pulse1 * 2} fill="none" stroke="#286CFF" strokeWidth="2" opacity={0.3 - pulse1 * 0.02} />
          <circle cx="0" cy="0" r={95 + pulse1 * 3} fill="none" stroke="#286CFF" strokeWidth="1.5" opacity={0.2 - pulse1 * 0.015} />
        </g>

        {/* Animated Employee Icons - Top Left */}
        <g transform={`translate(150, ${150 + float1Y})`}>
          <circle cx="0" cy="0" r="40" fill="white" filter="drop-shadow(0 4px 12px rgba(0,0,0,0.15))" />
          <circle cx="0" cy="-8" r="12" fill="#28C08A" />
          <rect x="-10" y="4" width="20" height="20" rx="3" fill="#286CFF" />
          <text x="0" y="32" textAnchor="middle" fontSize="11" fill="#7C8A9A" fontWeight="600">Team</text>
        </g>

        {/* Animated Health Icon - Top Right */}
        <g transform={`translate(450, ${150 + float2Y})`}>
          <circle cx="0" cy="0" r="40" fill="white" filter="drop-shadow(0 4px 12px rgba(0,0,0,0.15))" />
          <path d="M-12,-5 L-6,-12 L0,-5 L6,-12 L12,-5 L12,8 Q12,14 6,18 L0,22 L-6,18 Q-12,14 -12,8 Z"
            fill="#FF6B9D"
            transform={`rotate(${Math.sin(time * 2) * 5})`} />
          <text x="0" y="32" textAnchor="middle" fontSize="11" fill="#7C8A9A" fontWeight="600">Health</text>
        </g>

        {/* Animated Analytics Icon - Bottom Right */}
        <g transform={`translate(450, ${300 + float3Y})`}>
          <circle cx="0" cy="0" r="40" fill="white" filter="drop-shadow(0 4px 12px rgba(0,0,0,0.15))" />
          <rect x="-18" y="10" width="8" height={15 + pulse2} rx="2" fill="url(#greenGrad)" />
          <rect x="-6" y="5" width="8" height={20 + pulse3} rx="2" fill="url(#greenGrad)" />
          <rect x="6" y="-2" width="8" height={27 + pulse1} rx="2" fill="url(#greenGrad)" />
          <text x="0" y="42" textAnchor="middle" fontSize="11" fill="#7C8A9A" fontWeight="600">Analytics</text>
        </g>

        {/* Animated Security Shield - Bottom Left */}
        <g transform={`translate(150, ${300 + float1Y})`}>
          <circle cx="0" cy="0" r="40" fill="white" filter="drop-shadow(0 4px 12px rgba(0,0,0,0.15))" />
          <path d="M0,-18 L-14,-10 L-14,2 Q-14,12 0,20 Q14,12 14,2 L14,-10 Z"
            fill="url(#goldGrad)"
            transform={`scale(${1 + pulse2 * 0.02})`} />
          <path d="M-6,0 L-2,4 L6,-4" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          <text x="0" y="42" textAnchor="middle" fontSize="11" fill="#7C8A9A" fontWeight="600">Security</text>
        </g>

        {/* Animated Data Flow Particles */}
        {[0, 1, 2, 3, 4].map((i) => {
          const angle = (time * 30 + i * 72) % 360;
          const radius = 120;
          const x = 300 + Math.cos(angle * Math.PI / 180) * radius;
          const y = 225 + Math.sin(angle * Math.PI / 180) * radius;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="4"
              fill="#286CFF"
              opacity={0.4 + Math.sin(time + i) * 0.2}
            />
          );
        })}

        {/* Rotating outer data points */}
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
          const angle = (rotate + i * 45) % 360;
          const radius = 180;
          const x = 300 + Math.cos(angle * Math.PI / 180) * radius;
          const y = 225 + Math.sin(angle * Math.PI / 180) * radius;
          return (
            <g key={`outer-${i}`} opacity={0.6}>
              <circle cx={x} cy={y} r="6" fill="#28C08A" />
              <circle cx={x} cy={y} r={8 + Math.sin(time + i) * 2} fill="none" stroke="#28C08A" strokeWidth="1.5" opacity="0.3" />
            </g>
          );
        })}

        {/* Floating health symbols */}
        <g transform={`translate(100, ${80 + Math.sin(time * 0.6) * 15})`} opacity="0.7">
          <circle cx="0" cy="0" r="18" fill="white" filter="drop-shadow(0 2px 6px rgba(0,0,0,0.1))" />
          <text x="0" y="6" textAnchor="middle" fontSize="20">💊</text>
        </g>

        <g transform={`translate(500, ${100 + Math.sin(time * 0.7 + 1) * 12})`} opacity="0.7">
          <circle cx="0" cy="0" r="18" fill="white" filter="drop-shadow(0 2px 6px rgba(0,0,0,0.1))" />
          <text x="0" y="6" textAnchor="middle" fontSize="20">🩺</text>
        </g>

        <g transform={`translate(80, ${350 + Math.sin(time * 0.5 + 2) * 10})`} opacity="0.7">
          <circle cx="0" cy="0" r="16" fill="white" filter="drop-shadow(0 2px 6px rgba(0,0,0,0.1))" />
          <text x="0" y="5" textAnchor="middle" fontSize="18">🏥</text>
        </g>

        <g transform={`translate(520, ${330 + Math.sin(time * 0.8 + 3) * 13})`} opacity="0.7">
          <circle cx="0" cy="0" r="16" fill="white" filter="drop-shadow(0 2px 6px rgba(0,0,0,0.1))" />
          <text x="0" y="5" textAnchor="middle" fontSize="18">💉</text>
        </g>
      </svg>
    </div>
  );
};

const OrganizationsPremium = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    employeeCount: '',
    message: ''
  });

  const [customPlan, setCustomPlan] = useState({
    consultations: true,
    pharmacy: false,
    labTests: false,
    insurance: false,
    mentalWellness: false,
    ambulance: false,
    wellness: false,
    employeeSize: '50-200'
  });

  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [roiEmployees, setRoiEmployees] = useState(100);
  const [animatedStats, setAnimatedStats] = useState({
    partners: 0,
    employees: 0,
    consultations: 0,
    rating: 0
  });

  const statsRef = useRef(null);
  const [statsAnimated, setStatsAnimated] = useState(false);

  // Animated counter effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !statsAnimated) {
          setStatsAnimated(true);
          animateStats();
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [statsAnimated]);

  const animateStats = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = {
      partners: 500,
      employees: 1000000,
      consultations: 50000,
      rating: 4.8
    };

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setAnimatedStats({
        partners: Math.floor(targets.partners * progress),
        employees: Math.floor(targets.employees * progress),
        consultations: Math.floor(targets.consultations * progress),
        rating: (targets.rating * progress).toFixed(1)
      });

      if (step >= steps) {
        clearInterval(timer);
        setAnimatedStats(targets);
      }
    }, interval);
  };

  // Sticky CTA bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePlanChange = (feature) => {
    setCustomPlan({
      ...customPlan,
      [feature]: !customPlan[feature]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData, customPlan);
    alert('Thank you! Our team will contact you within 24 hours.');
  };

  // Custom SVG Icons
  const StethoscopeIcon = () => (
    <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id="stethGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#286CFF', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#4A85FF', stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <circle cx="50" cy="75" r="12" fill="url(#stethGrad)" />
      <path d="M50 75 L50 40 M50 40 Q30 30 25 20 M50 40 Q70 30 75 20" stroke="url(#stethGrad)" strokeWidth="4" fill="none" strokeLinecap="round" />
      <circle cx="25" cy="20" r="8" fill="url(#stethGrad)" />
      <circle cx="75" cy="20" r="8" fill="url(#stethGrad)" />
    </svg>
  );

  const PillIcon = () => (
    <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id="pillGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#FF6B9D', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#FF8FB5', stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <rect x="25" y="35" width="50" height="30" rx="15" fill="url(#pillGrad)" />
      <rect x="25" y="35" width="25" height="30" fill="#FF6B9D" opacity="0.8" />
      <line x1="50" y1="35" x2="50" y2="65" stroke="white" strokeWidth="2" />
    </svg>
  );

  const FlaskIcon = () => (
    <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id="flaskGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#28C08A', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#20A070', stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <path d="M40 20 L40 40 L25 70 Q20 80 30 85 L70 85 Q80 80 75 70 L60 40 L60 20 Z" fill="url(#flaskGrad)" />
      <rect x="35" y="15" width="30" height="5" rx="2" fill="#28C08A" />
      <ellipse cx="50" cy="70" rx="15" ry="8" fill="#20A070" opacity="0.5" />
    </svg>
  );

  const services = [
    {
      icon: <StethoscopeIcon />,
      title: 'Online Consultations',
      description: '24/7 access to 25+ specialities with verified doctors for comprehensive employee care',
      color: '#286CFF'
    },
    {
      icon: <PillIcon />,
      title: 'Online Pharmacy',
      description: 'Medicine delivery with up to 20% discount on prescriptions and OTC medications',
      color: '#FF6B9D'
    },
    {
      icon: <FlaskIcon />,
      title: 'Lab Tests at Home',
      description: 'NABL-accredited diagnostic tests with home collection at discounted rates',
      color: '#28C08A'
    },
    {
      icon: (
        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FFD28A', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#FFB84D', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <path d="M50 10 L20 25 L20 50 Q20 75 50 90 Q80 75 80 50 L80 25 Z" fill="url(#shieldGrad)" />
          <path d="M40 50 L45 55 L60 40" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      ),
      title: 'Group Health Insurance',
      description: 'Comprehensive coverage for employees and their families with cashless claims',
      color: '#FFD28A'
    },
    {
      icon: (
        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="ambulanceGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#FF6B6B', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#FF8787', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <rect x="20" y="40" width="55" height="30" rx="5" fill="url(#ambulanceGrad)" />
          <rect x="60" y="35" width="15" height="25" fill="#FF6B6B" />
          <circle cx="35" cy="72" r="8" fill="#333" />
          <circle cx="65" cy="72" r="8" fill="#333" />
          <path d="M45 50 L45 60 M40 55 L50 55" stroke="white" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ),
      title: 'Emergency Ambulance',
      description: '24/7 emergency ambulance service with trained medical staff',
      color: '#FF6B6B'
    },
    {
      icon: (
        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="brainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#9B7EFF', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#B49FFF', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <ellipse cx="50" cy="50" rx="30" ry="35" fill="url(#brainGrad)" />
          <path d="M30 40 Q35 35 40 40 Q45 35 50 40 Q55 35 60 40 Q65 35 70 40" stroke="#8B6EEF" strokeWidth="2" fill="none" />
          <path d="M30 55 Q35 50 40 55 Q45 50 50 55 Q55 50 60 55 Q65 50 70 55" stroke="#8B6EEF" strokeWidth="2" fill="none" />
        </svg>
      ),
      title: 'Mental Wellness',
      description: 'Therapy sessions, stress management, and wellbeing programs for employees',
      color: '#9B7EFF'
    },
    {
      icon: (
        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="dumbbellGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#FFB800', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#FFD28A', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <rect x="35" y="48" width="30" height="4" fill="url(#dumbbellGrad)" />
          <rect x="20" y="40" width="12" height="20" rx="2" fill="url(#dumbbellGrad)" />
          <rect x="68" y="40" width="12" height="20" rx="2" fill="url(#dumbbellGrad)" />
          <rect x="15" y="35" width="5" height="30" rx="2" fill="#FFB800" />
          <rect x="80" y="35" width="5" height="30" rx="2" fill="#FFB800" />
        </svg>
      ),
      title: 'Wellness Programs',
      description: 'Health webinars, fitness challenges, and employee engagement activities',
      color: '#FFB800'
    },
    {
      icon: (
        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="chartGrad" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#286CFF', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#4A85FF', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <rect x="20" y="60" width="15" height="25" rx="2" fill="url(#chartGrad)" />
          <rect x="42" y="45" width="15" height="40" rx="2" fill="url(#chartGrad)" />
          <rect x="64" y="30" width="15" height="55" rx="2" fill="url(#chartGrad)" />
          <path d="M27 55 L50 40 L72 25" stroke="#28C08A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      ),
      title: 'Health Analytics',
      description: 'Real-time dashboard to track employee health metrics and program utilization',
      color: '#286CFF'
    }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'HR Director',
      company: 'Tata Consultancy',
      rating: 5,
      text: 'GEWO Health transformed our employee wellness program. We saw a 40% reduction in sick leaves and significant improvement in employee satisfaction scores.',
      logo: '🏢'
    },
    {
      name: 'Priya Sharma',
      role: 'Chief People Officer',
      company: 'OPPO India',
      rating: 5,
      text: 'The implementation was seamless and the ROI has been impressive. Our employees love the 24/7 consultation access and mental wellness programs.',
      logo: '📱'
    },
    {
      name: 'Amit Patel',
      role: 'Benefits Manager',
      company: 'Aakash Educational',
      rating: 5,
      text: 'Best decision we made for employee benefits. The plan customization and real-time analytics give us complete control and visibility.',
      logo: '📚'
    }
  ];

  const timeline = [
    {
      icon: Phone,
      title: 'Initial Consultation',
      description: 'Schedule a call with our team to discuss your requirements',
      duration: 'Day 1'
    },
    {
      icon: Target,
      title: 'Custom Plan Design',
      description: 'We create a tailored benefits package for your organization',
      duration: 'Day 2-3'
    },
    {
      icon: Users,
      title: 'Employee Onboarding',
      description: 'Seamless enrollment process with dedicated support',
      duration: 'Day 4-7'
    },
    {
      icon: Zap,
      title: 'Go Live',
      description: 'Your team starts accessing benefits immediately',
      duration: 'Day 7-10'
    }
  ];

  const calculateROI = () => {
    const avgCostPerEmployee = 500; // Monthly healthcare cost
    const savingsPercent = 30;
    const monthlySavings = (roiEmployees * avgCostPerEmployee * savingsPercent) / 100;
    const annualSavings = monthlySavings * 12;
    return { monthlySavings, annualSavings };
  };

  const roi = calculateROI();

  const planFeatures = [
    { id: 'consultations', name: 'Online Consultations', basePrice: 299 },
    { id: 'pharmacy', name: 'Pharmacy Benefits', basePrice: 199 },
    { id: 'labTests', name: 'Lab Test Discounts', basePrice: 149 },
    { id: 'insurance', name: 'Group Insurance', basePrice: 999 },
    { id: 'mentalWellness', name: 'Mental Wellness', basePrice: 399 },
    { id: 'ambulance', name: 'Emergency Ambulance', basePrice: 299 },
    { id: 'wellness', name: 'Wellness Programs', basePrice: 249 }
  ];

  const employeeSizes = [
    { value: '1-50', label: '1-50 employees', multiplier: 1 },
    { value: '50-200', label: '50-200 employees', multiplier: 0.9 },
    { value: '200-500', label: '200-500 employees', multiplier: 0.8 },
    { value: '500+', label: '500+ employees', multiplier: 0.7 }
  ];

  const calculatePrice = () => {
    const selectedFeatures = planFeatures.filter(f => customPlan[f.id]);
    const baseTotal = selectedFeatures.reduce((sum, f) => sum + f.basePrice, 0);
    const multiplier = employeeSizes.find(s => s.value === customPlan.employeeSize)?.multiplier || 1;
    return Math.round(baseTotal * multiplier);
  };

  const comparisonPlans = [
    {
      name: 'Starter',
      price: '₹199',
      description: 'Perfect for small teams',
      features: ['Online Consultations', 'Pharmacy Discounts', 'Email Support', 'Basic Analytics'],
      highlighted: false
    },
    {
      name: 'Professional',
      price: '₹499',
      description: 'Most popular choice',
      features: ['Everything in Starter', 'Lab Tests', 'Mental Wellness', 'Priority Support', 'Advanced Analytics', 'Dedicated Account Manager'],
      highlighted: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'For large organizations',
      features: ['Everything in Professional', 'Group Insurance', 'Emergency Ambulance', 'Wellness Programs', 'Custom Integrations', 'White-label Option'],
      highlighted: false
    }
  ];

  const faqs = [
    {
      q: 'Are the benefits applicable to employees\' families?',
      a: 'Yes! Our corporate plans cover employees and their family members (self + 4 adults + 2 children). Each family member gets individual access to all benefits.'
    },
    {
      q: 'How quickly can we get started?',
      a: 'Implementation typically takes 7-10 business days. We handle everything from setup to employee onboarding with dedicated support.'
    },
    {
      q: 'Can we customize the benefits package?',
      a: 'Absolutely! Use our plan customizer to select exactly which benefits you want. We can also create fully custom packages for your specific needs.'
    },
    {
      q: 'What kind of reporting do you provide?',
      a: 'You get access to a real-time dashboard showing utilization metrics, health trends, cost savings, and employee engagement data.'
    },
    {
      q: 'Is there a minimum employee count?',
      a: 'We work with organizations of all sizes, from startups with 10 employees to large enterprises with thousands of employees.'
    },
    {
      q: 'How does pricing work?',
      a: 'Pricing is per employee per month and scales down as your team grows. Volume discounts apply automatically for teams of 50+ employees.'
    }
  ];

  return (
    <div style={styles.page}>
      <Navbar />

      {/* Enhanced Hero Section - GEWO Corporate Branding */}
      <section style={{
        background: 'linear-gradient(135deg, #1A4AC5 0%, #205BF3 100%)',
        padding: 'clamp(100px, 15vw, 140px) 16px clamp(60px, 10vw, 100px)',
        marginTop: '70px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Professional Grid Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }} />

        {/* Subtle Accent Elements */}
        <div style={{
          position: 'absolute',
          top: '15%',
          right: '8%',
          width: '280px',
          height: '280px',
          background: 'radial-gradient(circle, rgba(254, 208, 123, 0.06) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '220px',
          height: '220px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.04) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />

        <div style={styles.container}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div style={{ color: 'white', width: '100%', maxWidth: '850px' }}>
              {/* Corporate Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(254, 208, 123, 0.12)',
                border: '1px solid rgba(254, 208, 123, 0.25)',
                borderRadius: '8px',
                padding: '10px 20px',
                marginBottom: '28px'
              }}>
                <Building2 size={18} color="#FED07B" />
                <span style={{
                  color: '#FED07B',
                  fontSize: '13px',
                  fontWeight: '600',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>For Organizations</span>
              </div>

              <h1 style={{
                fontSize: 'clamp(32px, 7vw, 52px)',
                fontWeight: '700',
                lineHeight: '1.2',
                marginBottom: '24px',
                letterSpacing: '-0.5px'
              }}>
                Healthcare Benefits That{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #FED07B 0%, #FFE4A8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Strengthen Your Team</span>
              </h1>

              <p style={{
                fontSize: 'clamp(16px, 3.5vw, 19px)',
                lineHeight: '1.7',
                marginBottom: '40px',
                opacity: 0.9,
                maxWidth: '680px',
                margin: '0 auto 40px'
              }}>
                Provide comprehensive wellness coverage that improves productivity, reduces absenteeism, and demonstrates your commitment to employee wellbeing.
              </p>

              {/* Streamlined Stats Row */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginBottom: '40px',
                flexWrap: 'wrap'
              }} ref={statsRef}>
                {[
                  { value: `${animatedStats.partners}+`, label: 'Corporate Partners' },
                  { value: `${(animatedStats.employees / 1000000).toFixed(1)}M+`, label: 'Employees Covered' },
                  { value: `${(animatedStats.consultations / 1000).toFixed(0)}K+`, label: 'Daily Consultations' },
                  { value: animatedStats.rating, label: 'Customer Rating' }
                ].map((stat, index) => (
                  <div key={index} style={{
                    padding: '18px 24px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center',
                    minWidth: '130px'
                  }}>
                    <div style={{
                      fontSize: '26px',
                      fontWeight: '700',
                      color: '#FED07B',
                      marginBottom: '4px'
                    }}>{stat.value}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div style={{
                display: 'flex',
                gap: '14px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  style={{
                    background: '#FED07B',
                    color: '#0B1220',
                    border: 'none',
                    padding: '16px 32px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  Schedule a Demo <ArrowRight size={18} />
                </button>
                <button
                  style={{
                    background: 'transparent',
                    color: 'white',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    padding: '14px 28px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => document.getElementById('roi-calculator').scrollIntoView({ behavior: 'smooth' })}
                >
                  <Calculator size={18} />
                  Calculate Savings
                </button>
              </div>
            </div>

            {/* Animated Hero Illustration */}
            <AnimatedHeroIllustration />
          </div>
        </div>
      </section>

      {/* Trusted By Section - Enhanced */}
      <section style={styles.trustedSection}>
        <div style={styles.container}>
          <p style={styles.trustedTitle}>Trusted by leading organizations</p>
          <div style={styles.trustedLogos}>
            {['Aviva', 'Tata', 'AON', 'OPPO', 'Aakash', 'Ketto'].map((company, index) => (
              <div key={index} style={styles.trustedLogo} className="trusted-logo">
                <div style={styles.logoWrapper}>{company}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Services Section */}
      <section style={styles.servicesSection}>
        <div style={styles.container}>
          <div style={styles.sectionBadge}>
            <Zap size={16} style={{ marginRight: '6px' }} />
            OUR SERVICES
          </div>
          <h2 style={styles.sectionTitle}>Comprehensive Healthcare Services</h2>
          <p style={styles.sectionSubtitle}>
            Everything your employees need for complete health and wellness
          </p>

          <div style={styles.servicesGrid}>
            {services.map((service, index) => (
              <div
                key={index}
                style={styles.serviceCard}
                className="service-card"
                data-color={service.color}
              >
                <div style={styles.serviceIconWrapper} className="service-icon">
                  {service.icon}
                </div>
                <h3 style={styles.serviceTitle}>{service.title}</h3>
                <p style={styles.serviceDescription}>{service.description}</p>
                <div style={{ ...styles.serviceAccent, background: service.color }}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: Testimonials Section */}
      <section style={styles.testimonialsSection}>
        <div style={styles.container}>
          <div style={styles.sectionBadge}>
            <Star size={16} style={{ marginRight: '6px' }} />
            CLIENT SUCCESS STORIES
          </div>
          <h2 style={styles.sectionTitle}>What Our Partners Say</h2>
          <p style={styles.sectionSubtitle}>
            Join hundreds of organizations trusting GEWO Health
          </p>

          <div style={styles.testimonialsGrid}>
            {testimonials.map((testimonial, index) => (
              <div key={index} style={styles.testimonialCard} className="testimonial-card">
                {/* Decorative Quote Mark */}
                <div style={{
                  position: 'absolute',
                  top: '24px',
                  right: '24px',
                  opacity: 0.1
                }}>
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="#286CFF">
                    <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 7.55228 14.017 7V3H19.017C20.6739 3 22.017 4.34315 22.017 6V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM5.01697 21L5.01697 18C5.01697 16.8954 5.9124 16 7.01697 16H10.017C10.5693 16 11.017 15.5523 11.017 15V9C11.017 8.44772 10.5693 8 10.017 8H6.01697C5.46468 8 5.01697 7.55228 5.01697 7V3H10.017C11.6738 3 13.017 4.34315 13.017 6V15C13.017 18.3137 10.3307 21 7.01697 21H5.01697Z" />
                  </svg>
                </div>

                <div style={styles.testimonialHeader}>
                  <div style={styles.testimonialLogo}>{testimonial.logo}</div>
                  <div style={styles.testimonialRating}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="#FFB800" color="#FFB800" />
                    ))}
                  </div>
                </div>
                <p style={styles.testimonialText}>"{testimonial.text}"</p>
                <div style={styles.testimonialFooter}>
                  <div style={styles.testimonialAvatar}>{testimonial.name.charAt(0)}</div>
                  <div style={styles.testimonialAuthor}>
                    <div style={styles.testimonialName}>{testimonial.name}</div>
                    <div style={styles.testimonialRole}>{testimonial.role}, {testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: Implementation Timeline */}
      <section style={styles.timelineSection}>
        <div style={styles.container}>
          <div style={styles.sectionBadge}>
            <Clock size={16} style={{ marginRight: '6px' }} />
            IMPLEMENTATION PROCESS
          </div>
          <h2 style={styles.sectionTitle}>Get Started in Just 10 Days</h2>
          <p style={styles.sectionSubtitle}>
            Simple, transparent process from consultation to go-live
          </p>

          <div style={styles.timelineContainer}>
            {timeline.map((step, index) => (
              <div key={index} style={styles.timelineItem} className="timeline-item">
                <div style={styles.timelineDot} className="timeline-dot">
                  <step.icon size={24} color="white" />
                </div>
                {index < timeline.length - 1 && <div style={styles.timelineLine}></div>}
                <div style={styles.timelineContent}>
                  <div style={styles.timelineDuration}>{step.duration}</div>
                  <h3 style={styles.timelineTitle}>{step.title}</h3>
                  <p style={styles.timelineDescription}>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: ROI Calculator */}
      <section style={{ ...styles.roiSection, ...(isMobile && { padding: '60px 0' }) }} id="roi-calculator">
        <div style={styles.container}>
          <div style={{
            ...styles.roiCard,
            ...(isMobile && {
              gridTemplateColumns: '1fr',
              padding: '24px',
              gap: '32px'
            })
          }}>
            <div style={styles.roiLeft}>
              <div style={{ ...styles.sectionBadge, marginBottom: '16px' }}>
                <Calculator size={16} style={{ marginRight: '6px' }} />
                ROI CALCULATOR
              </div>
              <h2 style={{
                ...styles.roiTitle,
                ...(isMobile && { fontSize: '28px' })
              }}>Calculate Your Potential Savings</h2>
              <p style={styles.roiSubtitle}>
                See how much your organization can save with GEWO Health
              </p>

              <div style={styles.roiSliderContainer}>
                <label style={styles.roiLabel}>
                  Number of Employees: <span style={styles.roiValue}>{roiEmployees}</span>
                </label>
                <input
                  type="range"
                  min="10"
                  max="1000"
                  step="10"
                  value={roiEmployees}
                  onChange={(e) => setRoiEmployees(parseInt(e.target.value))}
                  style={styles.roiSlider}
                  className="roi-slider"
                />
              </div>

              <div style={{
                ...styles.roiResults,
                ...(isMobile && { gridTemplateColumns: '1fr' })
              }}>
                <div style={styles.roiResultItem}>
                  <div style={styles.roiResultLabel}>Monthly Savings</div>
                  <div style={{ ...styles.roiResultValue, ...(isMobile && { fontSize: '28px' }) }}>₹{roi.monthlySavings.toLocaleString()}</div>
                </div>
                <div style={styles.roiResultItem}>
                  <div style={styles.roiResultLabel}>Annual Savings</div>
                  <div style={{ ...styles.roiResultValue, ...(isMobile && { fontSize: '28px' }) }} >₹{roi.annualSavings.toLocaleString()}</div>
                </div>
              </div>

              <button
                style={{
                  ...styles.roiCTA,
                  ...(isMobile && { width: '100%', justifyContent: 'center' })
                }}
                className="roi-cta"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Detailed Analysis <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </button>
            </div>

            <div style={{
              ...styles.roiRight,
              ...(isMobile && { padding: '20px 0', height: '250px' })
            }}>
              <svg viewBox="0 0 300 300" style={{ width: '100%', height: '100%' }}>
                <defs>
                  <linearGradient id="chartGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                    <stop offset="0%" style={{ stopColor: '#28C08A', stopOpacity: 0.8 }} />
                    <stop offset="100%" style={{ stopColor: '#28C08A', stopOpacity: 0.3 }} />
                  </linearGradient>
                </defs>
                <circle cx="150" cy="150" r="120" fill="#E3EDFF" opacity="0.3" />
                <path d="M50 200 Q100 150 150 180 T250 100" stroke="#28C08A" strokeWidth="4" fill="none" />
                <path d="M50 200 Q100 150 150 180 T250 100 L250 250 L50 250 Z" fill="url(#chartGradient)" />
                {[{ x: 50, y: 200 }, { x: 150, y: 180 }, { x: 250, y: 100 }].map((point, i) => (
                  <circle key={i} cx={point.x} cy={point.y} r="8" fill="#28C08A" stroke="white" strokeWidth="3" />
                ))}
                <text x="150" y="280" textAnchor="middle" fill="#7C8A9A" fontSize="14">Projected Savings Growth</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Plans Comparison Table */}
      <section style={styles.comparisonSection}>
        <div style={styles.container}>
          <div style={styles.sectionBadge}>
            <TrendingUp size={16} style={{ marginRight: '6px' }} />
            PRICING PLANS
          </div>
          <h2 style={styles.sectionTitle}>Choose the Right Plan for Your Team</h2>
          <p style={styles.sectionSubtitle}>
            Flexible plans that scale with your organization
          </p>

          <div style={styles.comparisonGrid}>
            {comparisonPlans.map((plan, index) => (
              <div
                key={index}
                style={{
                  ...styles.comparisonCard,
                  ...(plan.highlighted ? styles.comparisonCardHighlighted : {})
                }}
                className="comparison-card"
              >
                {plan.highlighted && (
                  <div style={styles.popularBadge}>
                    <Star size={14} style={{ marginRight: '4px' }} fill="white" />
                    MOST POPULAR
                  </div>
                )}
                <h3 style={styles.comparisonPlanName}>{plan.name}</h3>
                <div style={styles.comparisonPrice}>{plan.price}</div>
                <p style={styles.comparisonDescription}>{plan.description}</p>
                <ul style={styles.comparisonFeatures}>
                  {plan.features.map((feature, i) => (
                    <li key={i} style={styles.comparisonFeature}>
                      <Check size={18} style={{ marginRight: '8px', color: '#28C08A', flexShrink: 0 }} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  style={{
                    ...styles.comparisonCTA,
                    ...(plan.highlighted ? styles.comparisonCTAHighlighted : {})
                  }}
                  className="comparison-cta"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Plan Customizer - Keep existing but with better styling */}
      <section style={styles.customizerSection}>
        <div style={styles.container}>
          <div style={styles.sectionBadge}>
            <Target size={16} style={{ marginRight: '6px' }} />
            CUSTOMIZE YOUR PLAN
          </div>
          <h2 style={styles.sectionTitle}>Build Your Perfect Benefits Package</h2>
          <p style={styles.sectionSubtitle}>
            Select the benefits you need and see pricing in real-time
          </p>

          <div style={styles.customizerCard}>
            <div style={styles.customizerLeft}>
              <h3 style={styles.customizerSubtitle}>Select Benefits</h3>

              <div style={styles.benefitsList}>
                {planFeatures.map((feature) => (
                  <div
                    key={feature.id}
                    style={{
                      ...styles.benefitItem,
                      ...(customPlan[feature.id] ? styles.benefitItemActive : {})
                    }}
                    className="benefit-item"
                    onClick={() => handlePlanChange(feature.id)}
                  >
                    <div style={{
                      ...styles.benefitCheckbox,
                      ...(customPlan[feature.id] ? styles.benefitCheckboxChecked : {})
                    }}>
                      {customPlan[feature.id] && <Check size={18} color="#133E97" strokeWidth={3} />}
                    </div>
                    <div style={styles.benefitInfo}>
                      <div style={styles.benefitName}>{feature.name}</div>
                      <div style={styles.benefitPrice}>₹{feature.basePrice}/employee/month</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.employeeSizeSelector}>
                <label style={styles.employeeSizeLabel}>Company Size</label>
                <select
                  value={customPlan.employeeSize}
                  onChange={(e) => setCustomPlan({ ...customPlan, employeeSize: e.target.value })}
                  style={styles.employeeSizeSelect}
                  className="employee-size-select"
                >
                  {employeeSizes.map((size) => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.customizerRight}>
              <div style={styles.pricingSummary}>
                <h3 style={styles.pricingSummaryTitle}>Your Custom Plan</h3>

                <div style={styles.selectedBenefits}>
                  {planFeatures
                    .filter(f => customPlan[f.id])
                    .map((feature) => (
                      <div key={feature.id} style={styles.selectedBenefit}>
                        <span>{feature.name}</span>
                        <span>₹{feature.basePrice}</span>
                      </div>
                    ))}
                </div>

                {customPlan.employeeSize !== '1-50' && (
                  <div style={styles.discountRow}>
                    <span>Volume Discount</span>
                    <span style={{ color: '#28C08A' }}>
                      {((1 - employeeSizes.find(s => s.value === customPlan.employeeSize)?.multiplier) * 100).toFixed(0)}% OFF
                    </span>
                  </div>
                )}

                <div style={styles.totalPrice}>
                  <span>Per Employee/Month</span>
                  <span style={styles.totalPriceAmount}>₹{calculatePrice()}</span>
                </div>

                <button
                  style={styles.customPlanCTA}
                  className="custom-plan-cta"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  Request This Plan <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced FAQ Section */}
      <section style={styles.faqSection}>
        <div style={styles.container}>
          <div style={styles.sectionBadge}>
            <Shield size={16} style={{ marginRight: '6px' }} />
            FREQUENTLY ASKED QUESTIONS
          </div>
          <h2 style={styles.sectionTitle}>Got Questions? We've Got Answers</h2>
          <p style={styles.sectionSubtitle}>
            Everything you need to know about our corporate wellness plans
          </p>

          <div style={styles.faqContainer}>
            {faqs.map((faq, index) => (
              <div key={index} style={styles.faqItem} className="faq-item">
                <div
                  style={styles.faqQuestion}
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="faq-question"
                >
                  <h3 style={styles.faqQuestionText}>{faq.q}</h3>
                  <div style={styles.faqIcon}>
                    {expandedFaq === index ? <Minus size={20} /> : <Plus size={20} />}
                  </div>
                </div>
                {expandedFaq === index && (
                  <div style={styles.faqAnswer} className="faq-answer">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Contact Form */}
      <section style={styles.contactSection} id="contact">
        <div style={styles.container}>
          <div style={styles.contactGrid}>
            <div style={styles.contactLeft}>
              <div style={{ ...styles.sectionBadge, marginBottom: '16px' }}>
                <Phone size={16} style={{ marginRight: '6px' }} />
                GET IN TOUCH
              </div>
              <h2 style={styles.contactTitle}>Ready to Transform Your Workplace Wellness?</h2>
              <p style={styles.contactSubtitle}>
                Schedule a consultation with our team and discover how GEWO Health can revolutionize your employee benefits.
              </p>

              <div style={styles.contactInfo}>
                <div style={styles.contactInfoItem}>
                  <div style={styles.contactInfoIcon}>
                    <Phone size={20} />
                  </div>
                  <div>
                    <div style={styles.contactInfoLabel}>Call Us</div>
                    <div style={styles.contactInfoValue}>+91 (555) 123-4567</div>
                  </div>
                </div>
                <div style={styles.contactInfoItem}>
                  <div style={styles.contactInfoIcon}>
                    <Mail size={20} />
                  </div>
                  <div>
                    <div style={styles.contactInfoLabel}>Email Us</div>
                    <div style={styles.contactInfoValue}>corporate@gewohealth.com</div>
                  </div>
                </div>
                <div style={styles.contactInfoItem}>
                  <div style={styles.contactInfoIcon}>
                    <Clock size={20} />
                  </div>
                  <div>
                    <div style={styles.contactInfoLabel}>Business Hours</div>
                    <div style={styles.contactInfoValue}>Mon-Fri: 9 AM - 6 PM</div>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.contactRight}>
              <form style={styles.contactForm} onSubmit={handleSubmit}>
                <div style={styles.formGroup}>
                  <input
                    type="text"
                    name="companyName"
                    placeholder="Company Name *"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    style={styles.formInput}
                    className="form-input"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <input
                    type="text"
                    name="contactPerson"
                    placeholder="Contact Person *"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    style={styles.formInput}
                    className="form-input"
                    required
                  />
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={handleInputChange}
                      style={styles.formInput}
                      className="form-input"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number *"
                      value={formData.phone}
                      onChange={handleInputChange}
                      style={styles.formInput}
                      className="form-input"
                      required
                    />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <input
                    type="text"
                    name="employeeCount"
                    placeholder="Number of Employees *"
                    value={formData.employeeCount}
                    onChange={handleInputChange}
                    style={styles.formInput}
                    className="form-input"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <textarea
                    name="message"
                    placeholder="Tell us about your requirements..."
                    value={formData.message}
                    onChange={handleInputChange}
                    style={styles.formTextarea}
                    className="form-input"
                    rows="4"
                  />
                </div>
                <button type="submit" style={styles.formSubmit} className="form-submit">
                  Schedule Demo <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky CTA Bar */}
      {showStickyCTA && (
        <div style={styles.stickyCTA} className="sticky-cta">
          <div style={styles.stickyContent}>
            <div style={styles.stickyText}>
              <strong>Ready to get started?</strong>
              <span style={styles.stickySubtext}>Transform your employee wellness today</span>
            </div>
            <button
              style={styles.stickyButton}
              className="sticky-button"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Schedule Demo <ArrowRight size={18} style={{ marginLeft: '6px' }} />
            </button>
          </div>
        </div>
      )}

      <Footer />

      {/* Enhanced CSS */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-10px) translateX(5px); }
          50% { transform: translateY(0) translateX(-5px); }
          75% { transform: translateY(10px) translateX(0); }
        }

        @keyframes floatParticle {
          0%, 100% { transform: translate(0, 0); opacity: 0.5; }
          50% { transform: translate(20px, -20px); opacity: 0.8; }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        .premium-badge {
          animation: fadeInUp 0.6s ease-out;
        }

        .stat-item {
          animation: fadeInUp 0.8s ease-out;
        }

        .stat-item:nth-child(2) { animation-delay: 0.1s; }
        .stat-item:nth-child(3) { animation-delay: 0.2s; }
        .stat-item:nth-child(4) { animation-delay: 0.3s; }

        /* Simplified entrance animation */
        .hero-illustration {
          animation: fadeInUp 0.8s ease-out backwards;
        }

        @keyframes floatSlow {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-15px) translateX(5px); }
          50% { transform: translateY(0) translateX(10px); }
          75% { transform: translateY(15px) translateX(5px); }
        }

        @keyframes floatMedium {
          0%, 100% { transform: translateY(0) translateX(0) rotate(0deg); }
          25% { transform: translateY(-12px) translateX(-5px) rotate(2deg); }
          50% { transform: translateY(0) translateX(-8px) rotate(0deg); }
          75% { transform: translateY(12px) translateX(-5px) rotate(-2deg); }
        }

        @keyframes floatFast {
          0%, 100% { transform: translateY(0) scale(1); }
          25% { transform: translateY(-10px) scale(1.05); }
          50% { transform: translateY(0) scale(1); }
          75% { transform: translateY(10px) scale(0.95); }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.8);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }

        @keyframes rotate360 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes shimmer {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }

        @keyframes slideInFromLeft {
          from {
            transform: translateX(-100px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideInFromRight {
          from {
            transform: translateX(100px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .service-card {
          animation: fadeInUp 0.6s ease-out backwards;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .service-card:nth-child(1) { animation-delay: 0.1s; }
        .service-card:nth-child(2) { animation-delay: 0.2s; }
        .service-card:nth-child(3) { animation-delay: 0.3s; }
        .service-card:nth-child(4) { animation-delay: 0.4s; }
        .service-card:nth-child(5) { animation-delay: 0.5s; }
        .service-card:nth-child(6) { animation-delay: 0.6s; }
        .service-card:nth-child(7) { animation-delay: 0.7s; }
        .service-card:nth-child(8) { animation-delay: 0.8s; }

        .service-card:hover {
          transform: translateY(-12px) scale(1.02);
          box-shadow: 0 20px 60px rgba(40, 108, 255, 0.15), 0 0 0 3px rgba(40, 108, 255, 0.1);
        }

        .service-card:hover .service-icon {
          transform: scale(1.15) rotate(-5deg);
        }

        .testimonial-card {
          animation: fadeInUp 0.6s ease-out backwards;
          transition: all 0.3s ease;
        }

        .testimonial-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
        }

        .timeline-item {
          animation: fadeInUp 0.6s ease-out backwards;
        }

        .timeline-item:nth-child(1) { animation-delay: 0.1s; }
        .timeline-item:nth-child(2) { animation-delay: 0.2s; }
        .timeline-item:nth-child(3) { animation-delay: 0.3s; }
        .timeline-item:nth-child(4) { animation-delay: 0.4s; }

        .timeline-dot {
          transition: all 0.3s ease;
        }

        .timeline-item:hover .timeline-dot {
          transform: scale(1.15);
          box-shadow: 0 8px 24px rgba(40, 108, 255, 0.3);
        }

        .comparison-card {
          transition: all 0.3s ease;
        }

        .comparison-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .benefit-item {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .benefit-item:hover {
          transform: translateX(4px);
          box-shadow: 0 4px 16px rgba(40, 108, 255, 0.1);
          border-color: #286CFF;
        }

        .benefit-item:active {
          transform: translateX(2px);
        }

        .faq-item {
          transition: all 0.3s ease;
        }

        .faq-question:hover {
          background: #F4F7FB;
        }

        .faq-answer {
          animation: fadeInUp 0.3s ease-out;
        }

        .form-input:focus {
          border-color: #286CFF;
          box-shadow: 0 0 0 3px rgba(40, 108, 255, 0.1);
        }

        .hero-cta-primary:hover {
          background: linear-gradient(135deg, #FFD98E 0%, #F4CE7B 100%);
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(244, 206, 123, 0.4);
        }

        .hero-cta-secondary:hover {
          background: white;
          border-color: #286CFF;
          color: #286CFF;
          transform: translateY(-2px);
        }

        .roi-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #286CFF;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(40, 108, 255, 0.3);
          transition: all 0.2s ease;
        }

        .roi-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 6px 16px rgba(40, 108, 255, 0.4);
        }

        .roi-slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #286CFF;
          cursor: pointer;
          border: none;
          box-shadow: 0 4px 12px rgba(40, 108, 255, 0.3);
          transition: all 0.2s ease;
        }

        .roi-slider::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 6px 16px rgba(40, 108, 255, 0.4);
        }

        .sticky-cta {
          animation: slideInRight 0.4s ease-out;
        }

        .trusted-logo:hover .logoWrapper {
          transform: scale(1.05);
          color: #286CFF;
        }

        @media (max-width: 768px) {
          .service-card {
            animation-delay: 0s !important;
          }
          
          .hero-illustration {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF'
  },

  // Hero Section
  heroSection: {
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 50%, #286CFF 100%)',
    padding: 'clamp(100px, 15vw, 140px) 16px clamp(60px, 10vw, 100px)',
    marginTop: '70px',
    position: 'relative',
    overflow: 'hidden'
  },
  heroBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none'
  },
  floatingParticle: {
    position: 'absolute',
    width: '4px',
    height: '4px',
    background: 'rgba(255, 255, 255, 0.6)',
    borderRadius: '50%',
    animation: 'floatParticle 4s ease-in-out infinite'
  },
  container: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: '0 16px',
    position: 'relative',
    zIndex: 1,
    boxSizing: 'border-box'
  },
  heroGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    alignItems: 'center',
    textAlign: 'center'
  },
  heroContent: {
    color: 'white',
    width: '100%'
  },
  premiumBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    borderRadius: '50px',
    fontSize: 'clamp(11px, 2.5vw, 13px)',
    fontWeight: '700',
    letterSpacing: '1px',
    marginBottom: '20px',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  heroTitle: {
    fontSize: 'clamp(28px, 7vw, 56px)',
    fontWeight: '800',
    lineHeight: '1.15',
    marginBottom: '16px',
    letterSpacing: '-0.5px'
  },
  heroTitleGradient: {
    background: 'linear-gradient(135deg, #FFD98E 0%, #F4CE7B 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  heroSubtitle: {
    fontSize: 'clamp(14px, 3.5vw, 18px)',
    lineHeight: '1.7',
    marginBottom: '32px',
    opacity: 0.95,
    maxWidth: '540px',
    margin: '0 auto 32px'
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '32px',
    padding: 'clamp(16px, 4vw, 32px)',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    width: '100%',
    boxSizing: 'border-box'
  },
  statItem: {
    textAlign: 'center'
  },
  statNumber: {
    fontSize: 'clamp(24px, 6vw, 36px)',
    fontWeight: '800',
    marginBottom: '4px',
    background: 'linear-gradient(135deg, #FFD98E 0%, #F4CE7B 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  statLabel: {
    fontSize: 'clamp(11px, 2.5vw, 13px)',
    opacity: 0.9,
    fontWeight: '500'
  },
  heroCTAs: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flexWrap: 'wrap',
    width: '100%',
    alignItems: 'center'
  },
  heroCta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(12px, 3vw, 16px) clamp(24px, 5vw, 32px)',
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    color: '#133E97',
    border: 'none',
    borderRadius: '50px',
    fontSize: 'clamp(14px, 3.5vw, 16px)',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(244, 206, 123, 0.3)',
    width: '100%',
    maxWidth: '280px'
  },
  heroCtaSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(12px, 3vw, 16px) clamp(24px, 5vw, 32px)',
    background: 'transparent',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50px',
    fontSize: 'clamp(14px, 3.5vw, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)',
    width: '100%',
    maxWidth: '280px'
  },
  heroIllustration: {
    position: 'relative',
    width: '100%',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center'
  },
  illustrationGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '400px',
    height: '400px',
    background: 'radial-gradient(circle, rgba(244, 206, 123, 0.15) 0%, transparent 70%)',
    borderRadius: '50%',
    pointerEvents: 'none'
  },

  // Trusted Section
  trustedSection: {
    padding: '40px 16px',
    background: '#F4F7FB'
  },
  trustedTitle: {
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#7C8A9A',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '32px'
  },
  trustedLogos: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '40px'
  },
  trustedLogo: {
    transition: 'all 0.3s ease'
  },
  logoWrapper: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#4A4A4A',
    padding: '16px 32px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease'
  },

  // Services Section
  servicesSection: {
    padding: '100px 0',
    background: 'white'
  },
  sectionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F4FF 100%)',
    color: '#286CFF',
    borderRadius: '50px',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '1px',
    marginBottom: '16px',
    border: '1px solid rgba(40, 108, 255, 0.15)'
  },
  sectionTitle: {
    fontSize: '48px',
    fontWeight: '800',
    color: '#1A202C',
    marginBottom: '16px',
    lineHeight: '1.2',
    textAlign: 'center',
    letterSpacing: '-0.5px'
  },
  sectionSubtitle: {
    fontSize: '18px',
    color: '#7C8A9A',
    marginBottom: '60px',
    textAlign: 'center',
    maxWidth: '600px',
    margin: '0 auto 60px'
  },
  servicesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '32px'
  },
  serviceCard: {
    background: 'white',
    border: '2px solid #E8ECF0',
    borderRadius: '20px',
    padding: '32px',
    position: 'relative',
    overflow: 'hidden'
  },
  serviceIconWrapper: {
    width: '80px',
    height: '80px',
    marginBottom: '24px',
    transition: 'all 0.3s ease'
  },
  serviceTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '12px'
  },
  serviceDescription: {
    fontSize: '15px',
    color: '#7C8A9A',
    lineHeight: '1.6'
  },
  serviceAccent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '4px',
    opacity: 0.3
  },

  // Testimonials Section
  testimonialsSection: {
    padding: '100px 0',
    background: '#F8FAFC',
    backgroundImage: `radial-gradient(circle at 10% 20%, rgba(40, 108, 255, 0.03) 0%, transparent 20%),
                      radial-gradient(circle at 90% 80%, rgba(40, 108, 255, 0.03) 0%, transparent 20%)`
  },
  testimonialsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '32px'
  },
  testimonialCard: {
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    position: 'relative',
    transition: 'all 0.3s ease'
  },
  testimonialHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    position: 'relative',
    zIndex: 1
  },
  testimonialLogo: {
    fontSize: '42px',
    filter: 'grayscale(0.2)'
  },
  testimonialRating: {
    display: 'flex',
    gap: '4px',
    background: 'rgba(255, 184, 0, 0.1)',
    padding: '6px 10px',
    borderRadius: '8px'
  },
  testimonialText: {
    fontSize: '17px',
    color: '#334155',
    lineHeight: '1.7',
    marginBottom: '32px',
    position: 'relative',
    zIndex: 1,
    fontWeight: '500'
  },
  testimonialFooter: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    paddingTop: '24px',
    borderTop: '1px solid #F1F5F9'
  },
  testimonialAvatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #286CFF 0%, #1A4AC5 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    fontWeight: '700',
    boxShadow: '0 4px 12px rgba(40, 108, 255, 0.2)'
  },
  testimonialAuthor: {
    flex: 1
  },
  testimonialName: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: '4px'
  },
  testimonialRole: {
    fontSize: '14px',
    color: '#64748B',
    fontWeight: '500'
  },

  // Timeline Section
  timelineSection: {
    padding: '100px 0',
    background: 'white'
  },
  timelineContainer: {
    maxWidth: '900px',
    margin: '0 auto',
    position: 'relative'
  },
  timelineItem: {
    display: 'grid',
    gridTemplateColumns: '100px 1fr',
    gap: '32px',
    marginBottom: '48px',
    position: 'relative'
  },
  timelineDot: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(40, 108, 255, 0.25)',
    position: 'relative',
    zIndex: 2
  },
  timelineLine: {
    position: 'absolute',
    left: '40px',
    top: '80px',
    width: '2px',
    height: 'calc(100% + 48px)',
    background: 'linear-gradient(180deg, #286CFF 0%, #E8ECF0 100%)'
  },
  timelineContent: {
    paddingTop: '12px'
  },
  timelineDuration: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#286CFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px'
  },
  timelineTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '8px'
  },
  timelineDescription: {
    fontSize: '16px',
    color: '#7C8A9A',
    lineHeight: '1.6'
  },

  // ROI Section
  roiSection: {
    padding: '100px 0',
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 50%, #286CFF 100%)'
  },
  roiCard: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    background: 'white',
    borderRadius: '24px',
    padding: '60px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
  },
  roiLeft: {

  },
  roiTitle: {
    fontSize: '40px',
    fontWeight: '800',
    color: '#1A202C',
    marginBottom: '12px',
    lineHeight: '1.2'
  },
  roiSubtitle: {
    fontSize: '16px',
    color: '#7C8A9A',
    marginBottom: '40px',
    lineHeight: '1.6'
  },
  roiSliderContainer: {
    marginBottom: '40px'
  },
  roiLabel: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '600',
    color: '#4A4A4A',
    marginBottom: '16px'
  },
  roiValue: {
    color: '#286CFF',
    fontSize: '20px'
  },
  roiSlider: {
    width: '100%',
    height: '6px',
    borderRadius: '3px',
    background: '#E8ECF0',
    outline: 'none',
    appearance: 'none'
  },
  roiResults: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '32px'
  },
  roiResultItem: {
    padding: '24px',
    background: '#F4F7FB',
    borderRadius: '16px',
    border: '2px solid #E8ECF0'
  },
  roiResultLabel: {
    fontSize: '14px',
    color: '#7C8A9A',
    marginBottom: '8px',
    fontWeight: '500'
  },
  roiResultValue: {
    fontSize: '32px',
    fontWeight: '800',
    color: '#286CFF'
  },
  roiCTA: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '16px 32px',
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(40, 108, 255, 0.3)'
  },
  roiRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  // Comparison Section
  comparisonSection: {
    padding: '100px 0',
    background: 'white'
  },
  comparisonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '32px',
    maxWidth: '1100px',
    margin: '0 auto'
  },
  comparisonCard: {
    background: 'white',
    border: '2px solid #E8ECF0',
    borderRadius: '24px',
    padding: '40px 32px',
    position: 'relative',
    transition: 'all 0.3s ease'
  },
  comparisonCardHighlighted: {
    border: '2px solid #286CFF',
    boxShadow: '0 12px 40px rgba(40, 108, 255, 0.15)',
    transform: 'scale(1.05)'
  },
  popularBadge: {
    position: 'absolute',
    top: '-16px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 20px',
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 100%)',
    color: 'white',
    borderRadius: '50px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1px',
    whiteSpace: 'nowrap'
  },
  comparisonPlanName: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '12px',
    textAlign: 'center'
  },
  comparisonPrice: {
    fontSize: '48px',
    fontWeight: '800',
    color: '#286CFF',
    marginBottom: '8px',
    textAlign: 'center'
  },
  comparisonDescription: {
    fontSize: '14px',
    color: '#7C8A9A',
    marginBottom: '32px',
    textAlign: 'center'
  },
  comparisonFeatures: {
    listStyle: 'none',
    padding: 0,
    marginBottom: '32px'
  },
  comparisonFeature: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '15px',
    color: '#4A4A4A',
    marginBottom: '12px',
    lineHeight: '1.5'
  },
  comparisonCTA: {
    width: '100%',
    padding: '14px 24px',
    background: 'white',
    color: '#286CFF',
    border: '2px solid #286CFF',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  comparisonCTAHighlighted: {
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 100%)',
    color: 'white',
    border: 'none'
  },

  // Customizer Section
  customizerSection: {
    padding: '100px 0',
    background: '#F4F7FB'
  },
  customizerCard: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '40px',
    background: 'white',
    borderRadius: '24px',
    padding: '48px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
  },
  customizerLeft: {

  },
  customizerSubtitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '24px'
  },
  benefitsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '32px'
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 20px',
    background: 'white',
    border: '2px solid #E8ECF0',
    borderRadius: '12px',
    cursor: 'pointer'
  },
  benefitItemActive: {
    background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F4FF 100%)',
    borderColor: '#286CFF'
  },
  benefitCheckbox: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    border: '2px solid #E8ECF0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    background: 'white'
  },
  benefitCheckboxChecked: {
    background: '#F4CE7B',
    borderColor: '#F4CE7B',
    boxShadow: '0 2px 8px rgba(244, 206, 123, 0.3)'
  },
  benefitInfo: {
    flex: 1
  },
  benefitName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1A202C',
    marginBottom: '4px'
  },
  benefitPrice: {
    fontSize: '14px',
    color: '#7C8A9A'
  },
  employeeSizeSelector: {

  },
  employeeSizeLabel: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '600',
    color: '#4A4A4A',
    marginBottom: '12px'
  },
  employeeSizeSelect: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #E8ECF0',
    borderRadius: '12px',
    fontSize: '15px',
    color: '#1A202C',
    background: 'white',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease'
  },
  customizerRight: {
    position: 'sticky',
    top: '100px',
    height: 'fit-content'
  },
  pricingSummary: {
    background: 'linear-gradient(135deg, #F4F7FB 0%, #E8ECF0 100%)',
    borderRadius: '20px',
    padding: '32px',
    border: '2px solid #E8ECF0'
  },
  pricingSummaryTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '24px'
  },
  selectedBenefits: {
    marginBottom: '20px'
  },
  selectedBenefit: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '15px',
    color: '#4A4A4A',
    marginBottom: '12px'
  },
  discountRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '15px',
    fontWeight: '600',
    paddingBottom: '16px',
    marginBottom: '16px',
    borderBottom: '1px solid #E8ECF0'
  },
  totalPrice: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '24px',
    marginBottom: '24px',
    borderBottom: '2px solid #E8ECF0'
  },
  totalPriceAmount: {
    fontSize: '36px',
    fontWeight: '800',
    color: '#286CFF'
  },
  customPlanCTA: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(40, 108, 255, 0.3)'
  },

  // FAQ Section
  faqSection: {
    padding: '100px 0',
    background: 'white'
  },
  faqContainer: {
    maxWidth: '900px',
    margin: '0 auto'
  },
  faqItem: {
    background: 'white',
    border: '2px solid #E8ECF0',
    borderRadius: '16px',
    marginBottom: '16px',
    overflow: 'hidden'
  },
  faqQuestion: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 28px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  faqQuestionText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1A202C',
    flex: 1,
    marginRight: '20px'
  },
  faqIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#F4F7FB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#286CFF',
    flexShrink: 0
  },
  faqAnswer: {
    padding: '0 28px 24px',
    fontSize: '16px',
    color: '#7C8A9A',
    lineHeight: '1.7'
  },

  // Contact Section
  contactSection: {
    padding: '100px 0',
    background: '#F4F7FB'
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    alignItems: 'start'
  },
  contactLeft: {

  },
  contactTitle: {
    fontSize: '40px',
    fontWeight: '800',
    color: '#1A202C',
    marginBottom: '16px',
    lineHeight: '1.2'
  },
  contactSubtitle: {
    fontSize: '16px',
    color: '#7C8A9A',
    marginBottom: '40px',
    lineHeight: '1.6'
  },
  contactInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  contactInfoItem: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start'
  },
  contactInfoIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0
  },
  contactInfoLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#7C8A9A',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px'
  },
  contactInfoValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1A202C'
  },
  contactRight: {

  },
  contactForm: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
  },
  formGroup: {
    marginBottom: '20px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  formInput: {
    width: '100%',
    padding: '16px 20px',
    border: '2px solid #E8ECF0',
    borderRadius: '12px',
    fontSize: '15px',
    color: '#1A202C',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit'
  },
  formTextarea: {
    width: '100%',
    padding: '16px 20px',
    border: '2px solid #E8ECF0',
    borderRadius: '12px',
    fontSize: '15px',
    color: '#1A202C',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  formSubmit: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '18px',
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(40, 108, 255, 0.3)'
  },

  // Sticky CTA
  stickyCTA: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 100%)',
    padding: '20px 0',
    boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.15)',
    zIndex: 999
  },
  stickyContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  stickyText: {
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  stickySubtext: {
    fontSize: '14px',
    opacity: 0.9
  },
  stickyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    color: '#133E97',
    border: 'none',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 16px rgba(244, 206, 123, 0.3)'
  }
};

// Update active benefit checkbox
styles.benefitItemActive = {
  ...styles.benefitItemActive,
  '& .benefitCheckbox': {
    background: '#286CFF',
    borderColor: '#286CFF'
  }
};

export default OrganizationsPremium;
